import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GridColumnDef } from '../../models/work-order.model';

/**
 * "Edit Columns" button + dropdown checklist, styled to match the Work Order / Work Package
 * saved-views action buttons.
 *
 * Deliberately stateless about *which* columns are hidden — the owning page holds that array and
 * feeds it to both this button and its table. Keeping it here instead would tie the selection to
 * this component's lifetime, and callers that swap their table out while loading (skeleton
 * loaders) would silently lose the user's choices on every refresh.
 */
@Component({
  selector: 'app-column-chooser-button',
  templateUrl: './column-chooser-button.component.html',
  styleUrl: './column-chooser-button.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
  ],
})
export class ColumnChooserButtonComponent {
  /** The table's full column config; the selectable list is derived from it. */
  @Input() columnConfig: GridColumnDef[] = [];
  /** Fields currently hidden. Owned by the parent — this component only proposes changes. */
  @Input() hiddenColumns: string[] = [];
  @Output() hiddenColumnsChange = new EventEmitter<string[]>();

  /** "Find a column" filter, reset each time the menu opens. */
  readonly searchControl = new UntypedFormControl('');

  /** Only real, toggleable data columns — never the selection or action columns, nor ones the grid never shows. */
  get chooserColumns(): GridColumnDef[] {
    return (this.columnConfig || []).filter(
      (col) => !col.isCheckBox && col.type !== 'checkbox' && !col.isAction && !!col.field && col.visible !== false
    );
  }

  get filteredColumns(): GridColumnDef[] {
    const term = ((this.searchControl.value as string) || '').toLowerCase().trim();
    if (!term) return this.chooserColumns;
    return this.chooserColumns.filter((col) => (col.headerText || '').toLowerCase().includes(term));
  }

  /** Number of columns currently shown, for the "n of m" header count. */
  get visibleCount(): number {
    return this.chooserColumns.filter((col) => !this.isHidden(col.field)).length;
  }

  get totalCount(): number {
    return this.chooserColumns.length;
  }

  /** True when a "Find a column" term is narrowing the list. */
  /**
   * True when the grid is no longer showing every column — drives the dot on the trigger, so a
   * customised view is visible without opening the menu.
   */
  get hasHiddenColumns(): boolean {
    return this.visibleCount < this.totalCount;
  }

  get isFiltering(): boolean {
    return !!((this.searchControl.value as string) || '').trim();
  }

  isHidden(field: string | undefined): boolean {
    return !!field && this.hiddenColumns.includes(field);
  }

  // ===== Select all =====
  //
  // Scoped to the columns currently listed, so with a search active it acts on the matches only —
  // that's what the checkbox sitting above the filtered list reads as. Columns outside the current
  // filter keep whatever state they had.

  get allFilteredVisible(): boolean {
    const columns = this.filteredColumns;
    return columns.length > 0 && columns.every((col) => !this.isHidden(col.field));
  }

  /** Some but not all shown — drives the checkbox's indeterminate state. */
  get someFilteredVisible(): boolean {
    const columns = this.filteredColumns;
    return columns.some((col) => !this.isHidden(col.field)) && !this.allFilteredVisible;
  }

  toggleAll(): void {
    const fields = this.filteredColumns
      .map((col) => col.field)
      .filter((field): field is string => !!field);
    if (!fields.length) return;

    // Everything already shown -> this is a "deselect all"; otherwise select all (which is also
    // what an indeterminate checkbox should resolve to).
    const hide = this.allFilteredVisible;
    const next = new Set(this.hiddenColumns);
    fields.forEach((field) => (hide ? next.add(field) : next.delete(field)));

    this.hiddenColumnsChange.emit([...next]);
  }

  toggle(field: string | undefined): void {
    if (!field) return;
    this.hiddenColumnsChange.emit(
      this.isHidden(field)
        ? this.hiddenColumns.filter((f) => f !== field)
        : [...this.hiddenColumns, field]
    );
  }
}
